import { connect } from "react-redux";

import { unitHighlightAction, updateEmptyUnits } from "../../../Engine/ActionCreators/NavigationActionCreators";
import {
    addPageAction,
    clearPageAction,
    ClearPageMethod,
    closeModalUnitAction,
    openModalUnitAction,
    removePageAction,
} from "../../../Engine/ActionCreators/UnitActionCreators";
import { CandyDispatch } from "../../../Engine/Core/Store/CandyReduxTypes";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";
import { UnitStateSelect } from "../../../Helpers/StateAccessors/UnitStateSelect";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { EvaluableProp, withEvaluators } from "../../withEvaluators";
import { PathResolver, withResolvePaths } from "../../withResolvePaths";
import { withViewModelSelect } from "../../withViewModelSelect";
import { withVisibilityProps } from "../../withVisibilityProps";
import { withExternalMountingEventReporting } from "../../Services/ExternalMountingEventReporting/withExternalMountingEventReporting";
import { withUnitRegistry } from "../Navigation/UnitRegistry/UnitRegistryContext";
import { UnitIdResolver, withResolveUnitId } from "../../withResolveUnitId";
import { AbsoluteModelPath, AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { withCustomNormalizationHelper } from "../../withCustomNormalizationHelper";
import { CustomNormalizationHelper } from "../../../Engine/Core/Normalization/CustomNormalizationHelper";
import {
    FormActionHandlerExecutionInjectableProps,
    withFormActionHandlerExecution,
} from "../../Internals/FormActionHandlers/WithFormActionHandlerExecution";
import { FormActionHandler } from "../../Internals/FormActionHandlers/FormActionHandler";
import { getRemoveLabelForChangeSet } from "../UnitItem/UnitItemHelpers";
import { reject } from "../../../../../Common/TypingUtils";
import { UnitInfo } from "../../UnitInfo";

import { Unit } from "./Unit";
import {
    extractInstanceFromBindingPath,
    isUnitIdEquals,
    SectionName,
    UnitId,
    unitIdToLegacyUnitId,
} from "./UnitId/UnitId";
import { UnitIdSet } from "./UnitId/UnitIdImmutableSet";
import { PropsPageActions } from "./Types";

interface UnitContainerProps extends FormActionHandlerExecutionInjectableProps {
    bindingPath?: AbsoluteResolvedModelPath;
    unitId: UnitId;
    viewModelSelect: ViewModelSelect;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    visible?: EvaluableProp<boolean>;
    resolveUnitId: UnitIdResolver;
    multiple: boolean;
    customNormalizationHelper: CustomNormalizationHelper;
    unitsCountForPaging?: number;
    onMount?: FormActionHandler;
    onUnmount?: FormActionHandler;
    enableUploadFilesInPage: boolean;
    uploadFilesInPage?: FormActionHandler;
    name: string;
    modalInstancePageId?: string;
    resolvePath: PathResolver;
    pageActions: PropsPageActions;
}

const mapStateToProps = (state: FullFormState, ownProps: UnitContainerProps) => {
    const { bindingPath, viewModelSelect } = ownProps;
    const unitId = ownProps.resolveUnitId(ownProps.unitId);
    const maxWidth = UnitStateSelect.getUnitWidth(state, unitId);
    const resolvedUnitId = ownProps.resolveUnitId(ownProps.unitId);
    const { emptyUnits } = state;
    let visible = true;

    if (bindingPath != undefined) {
        const controlModel = viewModelSelect.pathViewModel(state, bindingPath);
        visible = controlModel.visible ?? true;
    }

    const modalUnit = state.modalUnits.find(x => x.targetUnitId.pageId === ownProps.unitId.pageId);
    const modalUnitId = modalUnit?.modalUnitId;
    const modalUnitTargetInstance = modalUnit?.targetUnitId?.instance;
    const filled = resolvedUnitId == undefined || !UnitIdSet.hasItem(emptyUnits, resolvedUnitId);
    const attachmentUnitItemInfoPaths = state.settings.attachmentUnitItemInfoPaths;

    if (attachmentUnitItemInfoPaths && state.settings.setUnitItemExtraInfo) {
        const value = Array.isArray(attachmentUnitItemInfoPaths)
            ? attachmentUnitItemInfoPaths.map(path => ownProps.viewModelSelect.customFieldValue(state, path, "value"))
            : ownProps.viewModelSelect.customFieldValue(state, attachmentUnitItemInfoPaths, "value");
        state.settings.setUnitItemExtraInfo(value);
    }

    return {
        unitId: unitId,
        hidden: !visible,
        maxWidth: maxWidth,
        unitsWidth: state.units || {},
        visible: state.settings.isReadOnly && !filled ? false : ownProps.processEvaluableProp(state, ownProps.visible),
        modalUnitId: modalUnitId,
        showUnitBorder: state.settings.showUnitBorder ?? true,
        modalUnitTargetInstance: modalUnitTargetInstance,
        isReadOnly: state.settings.isReadOnly,
        onRemoveAttachment: state.settings.onRemoveAttachment,
        onClearAttachment: state.settings.onClearAttachment,
        attachmentPageActions: state.settings.attachmentPageActions,
        enableUploadFilesInPage: ownProps.enableUploadFilesInPage,
        pageActions: ownProps.pageActions,
    };
};

function mapDispatchToProps(dispatch: CandyDispatch, ownProps: UnitContainerProps) {
    const resolveUnitWithHelpersBindingPath = (bindingPath: AbsoluteModelPath, unitInfos?: UnitInfo[]) => {
        const unitId = ownProps.multiple
            ? ownProps.resolveUnitId({
                  ...ownProps.unitId,
                  instance: extractInstanceFromBindingPath(bindingPath, ownProps.unitId, unitInfos),
              })
            : ownProps.resolveUnitId(ownProps.unitId);
        return unitId;
    };

    return {
        onAddPage: () => {
            dispatch(addPageAction(unitIdToLegacyUnitId(ownProps.resolveUnitId(ownProps.unitId)) as SectionName));
        },
        onRemovePage: async () => {
            dispatch(
                removePageAction(
                    unitIdToLegacyUnitId(ownProps.resolveUnitId(ownProps.unitId)) as SectionName,
                    ownProps.bindingPath
                )
            );
            await dispatch(async (_, __, form) => {
                await form.changeSet.send({ operationName: getRemoveLabelForChangeSet(ownProps.name) });
            });
        },
        onClearPage: (method: ClearPageMethod) => {
            dispatch(
                clearPageAction(
                    unitIdToLegacyUnitId(ownProps.resolveUnitId(ownProps.unitId)) as SectionName,
                    ownProps.bindingPath,
                    method
                )
            );
        },
        onHighlightUnit: (unitId: UnitId) => {
            dispatch(unitHighlightAction(unitId));
        },
        updateEmptyUnits: (unitId: UnitId, empty: boolean) => {
            dispatch(updateEmptyUnits(unitId, empty));
        },
        onEnableAutoValues: ownProps.customNormalizationHelper.isFillAutoFieldsFeatureEnabled()
            ? async () => {
                  if (ownProps.bindingPath != undefined) {
                      await ownProps.customNormalizationHelper.fillAutoFieldsWithAutoValuesAndReload([
                          ownProps.bindingPath,
                      ]);
                  }
              }
            : undefined,
        onMount: async () => {
            if (ownProps.onMount != undefined) {
                await ownProps.executeFormActionHandler(ownProps.onMount);
            }
        },
        onUnmount: async () => {
            if (ownProps.onUnmount != undefined) {
                await ownProps.executeFormActionHandler(ownProps.onUnmount);
            }
        },
        uploadFilesInPage: async () => {
            if (ownProps.uploadFilesInPage != undefined) {
                await ownProps.executeFormActionHandler(ownProps.uploadFilesInPage);
            }
        },
        onOpenModalRequest: async (helperContextualBindingPath: AbsoluteModelPath) => {
            const unitId = dispatch((_, getState) =>
                resolveUnitWithHelpersBindingPath(helperContextualBindingPath, getState().unitInfos)
            );

            dispatch(
                openModalUnitAction(
                    helperContextualBindingPath,
                    unitId,
                    ownProps.modalInstancePageId ?? reject("modalInstancePageId must not be undefined!")
                )
            );
        },
        onCloseModalRequest: async (helperContextualBindingPath: AbsoluteModelPath) => {
            const unitId = dispatch((_, getState) =>
                resolveUnitWithHelpersBindingPath(helperContextualBindingPath, getState().unitInfos)
            );

            dispatch(closeModalUnitAction(unitId));
        },
    };
}

export const UnitContainer = withResolvePaths(
    withResolveUnitId(
        withViewModelSelect(
            withEvaluators(
                withExternalMountingEventReporting(
                    withUnitRegistry(
                        withFormActionHandlerExecution(
                            withCustomNormalizationHelper(
                                connect(mapStateToProps, mapDispatchToProps, undefined, {
                                    areStatePropsEqual: (next, prev) =>
                                        isUnitIdEquals(next.unitId, prev.unitId) &&
                                        next.hidden === prev.hidden &&
                                        next.maxWidth === prev.maxWidth &&
                                        next.unitsWidth === prev.unitsWidth &&
                                        next.visible === prev.visible &&
                                        isUnitIdEquals(next.modalUnitId, prev.modalUnitId) &&
                                        next.modalUnitTargetInstance === prev.modalUnitTargetInstance &&
                                        next.showUnitBorder === prev.showUnitBorder,
                                })(withVisibilityProps(Unit))
                            )
                        )
                    )
                )
            )
        )
    )
);
